import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PX } from "@/constants/testIds";
import { createPxConcept, updatePxConcept } from "@/lib/pxMasterApi";

const CONTEXTS = [
  { id: "surgical-history", label: "Surgical History", testId: PX.formContextSurgicalHistory },
  { id: "procedures", label: "Procedures", testId: PX.formContextProcedures },
  { id: "orders", label: "Orders" },
  { id: "billing", label: "Billing" },
];

const BLANK = {
  localCode: "",
  display: "",
  shortDisplay: "",
  description: "",
  active: true,
  quickEntry: false,
  displayOrder: "",
  contexts: ["surgical-history"],
  mappings: [],
};

export default function PxMasterForm({ open, onClose, editing, mappingSystems, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [mappingDraft, setMappingDraft] = useState({ system: "", code: "", display: "", version: "" });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        localCode: editing.localCode || "",
        display: editing.display || "",
        shortDisplay: editing.shortDisplay || "",
        description: editing.description || "",
        active: editing.active !== false,
        quickEntry: !!editing.quickEntry,
        displayOrder: editing.displayOrder?.["surgical-history"] || editing.displayOrder?.["surgical-history-quick-entry"] || "",
        contexts: editing.contexts?.length ? editing.contexts : ["surgical-history"],
        mappings: (editing.mappings || []).map((row) => ({
          system: row.system,
          code: row.code,
          display: row.display || "",
          version: row.version || "",
          active: row.active !== false,
        })),
      });
    } else {
      setForm(BLANK);
    }
    setMappingDraft({
      system: mappingSystems[0]?.url || "",
      code: "",
      display: "",
      version: "",
    });
  }, [open, editing, mappingSystems]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleContext = (id) => {
    setForm((prev) => ({
      ...prev,
      contexts: prev.contexts.includes(id)
        ? prev.contexts.filter((row) => row !== id)
        : [...prev.contexts, id],
    }));
  };

  const addMapping = () => {
    if (!mappingDraft.system || !mappingDraft.code.trim()) {
      toast.error("Choose a registered terminology and enter a real code. Do not invent codes.");
      return;
    }
    setForm((prev) => ({
      ...prev,
      mappings: [
        ...prev.mappings,
        {
          system: mappingDraft.system,
          code: mappingDraft.code.trim(),
          display: mappingDraft.display.trim(),
          version: mappingDraft.version.trim() || undefined,
          active: true,
        },
      ],
    }));
    setMappingDraft((prev) => ({ ...prev, code: "", display: "", version: "" }));
  };

  const submit = async () => {
    if (!form.display.trim()) {
      toast.error("Display name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        display: form.display.trim(),
        shortDisplay: form.shortDisplay.trim() || form.display.trim(),
        description: form.description.trim() || form.display.trim(),
        active: form.active,
        quickEntry: form.quickEntry,
        contexts: form.contexts,
        mappings: form.mappings,
      };
      if (form.localCode.trim()) payload.localCode = form.localCode.trim().toUpperCase();
      if (form.displayOrder !== "" && form.displayOrder != null) {
        payload.displayOrder = Number(form.displayOrder);
      }
      if (editing) {
        await updatePxConcept(editing.id, payload);
        toast.success("Px concept updated.");
      } else {
        await createPxConcept(payload);
        toast.success("Px concept created.");
      }
      onSaved();
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Unable to save Px concept.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={PX.form}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Px concept" : "New Px concept"}</DialogTitle>
          <DialogDescription>
            Master terminology only. Patient surgical history remains a separate record that
            references this concept.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={PX.formLocalCode}>Local Px Code</Label>
            <Input
              id={PX.formLocalCode}
              data-testid={PX.formLocalCode}
              value={form.localCode}
              onChange={(event) => set("localCode")(event.target.value.toUpperCase())}
              placeholder="SHX3144 (assigned if blank)"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={PX.formDisplay}>Display</Label>
            <Input
              id={PX.formDisplay}
              data-testid={PX.formDisplay}
              value={form.display}
              onChange={(event) => set("display")(event.target.value)}
              placeholder="NEPHROSCOPY"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={PX.formShortDisplay}>Quick Entry label</Label>
            <Input
              id={PX.formShortDisplay}
              data-testid={PX.formShortDisplay}
              value={form.shortDisplay}
              onChange={(event) => set("shortDisplay")(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={PX.formDisplayOrder}>Display order</Label>
            <Input
              id={PX.formDisplayOrder}
              data-testid={PX.formDisplayOrder}
              type="number"
              value={form.displayOrder}
              onChange={(event) => set("displayOrder")(event.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={PX.formDescription}>Description</Label>
            <Textarea
              id={PX.formDescription}
              data-testid={PX.formDescription}
              value={form.description}
              onChange={(event) => set("description")(event.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid={PX.formActive}
              checked={form.active}
              onCheckedChange={set("active")}
            />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid={PX.formQuickEntry}
              checked={form.quickEntry}
              onCheckedChange={set("quickEntry")}
            />
            Surgical History Quick Entry
          </label>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-slate-800">Contexts</p>
          <div className="flex flex-wrap gap-4">
            {CONTEXTS.map((ctx) => (
              <label key={ctx.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  data-testid={ctx.testId}
                  checked={form.contexts.includes(ctx.id)}
                  onCheckedChange={() => toggleContext(ctx.id)}
                />
                {ctx.label}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-3">
          <p className="mb-2 text-sm font-medium text-slate-800">External terminology mappings</p>
          <p className="mb-3 text-xs text-slate-500">
            Optional. Use only verified SNOMED CT, CPT, HCPCS, or ICD-10-PCS codes. Do not invent codes.
          </p>
          <div className="grid gap-2 sm:grid-cols-4">
            <Select
              value={mappingDraft.system}
              onValueChange={(value) => setMappingDraft((prev) => ({ ...prev, system: value }))}
            >
              <SelectTrigger data-testid={PX.mappingSystem}>
                <SelectValue placeholder="System" />
              </SelectTrigger>
              <SelectContent>
                {mappingSystems.map((sys) => (
                  <SelectItem key={sys.id || sys.url} value={sys.url}>
                    {sys.title || sys.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid={PX.mappingCode}
              placeholder="Code"
              value={mappingDraft.code}
              onChange={(event) => setMappingDraft((prev) => ({ ...prev, code: event.target.value }))}
            />
            <Input
              placeholder="Display"
              value={mappingDraft.display}
              onChange={(event) => setMappingDraft((prev) => ({ ...prev, display: event.target.value }))}
            />
            <Button type="button" variant="outline" data-testid={PX.mappingAdd} onClick={addMapping}>
              Add mapping
            </Button>
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {form.mappings.map((row, index) => (
              <li key={`${row.system}-${row.code}-${index}`} className="flex items-center justify-between gap-2">
                <span className="truncate text-slate-700">
                  {row.code} · {row.display || row.system}
                </span>
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-800"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      mappings: prev.mappings.filter((_, i) => i !== index),
                    }))
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" data-testid={PX.formCancel} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" data-testid={PX.formSave} onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
